// Client-side re-implementation of the backend scoring so the example sheet
// ("przykladowy arkusz") can be fully interactive without ever hitting the
// server. The base task/group definitions come from the ungated /raw endpoints;
// the user's edits live in a localStorage "overlay". Everything derived
// (points, split points, achievedSymbol, overall category) is recomputed here.
//
// Ports of:
//   - backend/src/utils/taskcalc.ts                (calculateTaskScore)
//   - backend/src/routes/tasks/index.ts  getTasks  (two-pass scoring)
//   - backend/src/routes/categorization/index.ts   getCategory

// The display types are an exact copy of the real endpoints' contract, and the
// /raw endpoints return that same shape, so we share them rather than duplicate.
// import type is fully erased, so this pulls in no runtime code from Kategoryzacja.
import type { Task, Category, CategoryInfo, CategorizationDetails } from "../kategoryzacja/Kategoryzacja";

// The only thing that ever changes on the dummy sheet: per-task value/favourite
// and per-initial-task checkbox state. Persisted to localStorage; cleared on RESET.
export interface Overlay {
  tasks: { [id: number]: { value: number; favourite: boolean } };
  initial: { [id: number]: boolean };
}

export const emptyOverlay = (): Overlay => ({ tasks: {}, initial: {} });

// Port of backend/src/utils/taskcalc.ts (throws replaced with a safe 0 client-side)
export const calculateTaskScore = (type: Task["type"], value: number, maxPoints: number, multiplier: number | null, refVal: number | null): number => {
  if (type === "REFONLY") {
    return 0;
  } else if (type === "BOOLEAN") {
    if (value > 0) return maxPoints;
    return 0;
  } else if (type === "LINEAR") {
    if (multiplier == null) multiplier = 1;
    let val = value * multiplier;
    if (val > maxPoints) val = maxPoints;
    return val;
  } else if (type === "LINEAR_REF") {
    if (refVal === null || refVal === 0) return 0;
    let val = maxPoints * value / refVal;
    if (val > maxPoints) val = maxPoints;
    return val;
  } else if (type === "PARABOLIC_REF") {
    if (refVal === null || refVal === 0) return 0;
    let val = maxPoints * Math.pow(value / refVal, 2);
    if (val > maxPoints) val = maxPoints;
    return val;
  }
  return 0;
};

// Recompute every group's derived fields from the raw definitions + overlay.
// Mirrors getTasks: a first pass scores everything (REF tasks with a placeholder
// refVal of 1), then a second pass rescores REF tasks against the referenced
// task's actual value.
export const computeTasks = (base: Category[], overlay: Overlay): Category[] => {
  const firstPass = base.map((category) => {
    const tasks = category.tasks.map((task) => {
      const o = overlay.tasks[task.id];
      const value = o !== undefined ? o.value : 0;
      const favourite = o !== undefined ? (o.favourite || task.obligatory) : task.obligatory;

      const rawScore = calculateTaskScore(task.type, value, task.maxPoints, task.multiplier, 1);
      let primaryMaxPoints: number | undefined = undefined;
      let primaryPoints: number | undefined = undefined;
      let secondaryMaxPoints: number | undefined = undefined;
      let secondaryPoints: number | undefined = undefined;
      if (task.secondaryGroupId !== null) {
        primaryPoints = rawScore * task.split;
        secondaryPoints = rawScore * (1 - task.split);
        primaryMaxPoints = task.maxPoints * task.split;
        secondaryMaxPoints = task.maxPoints * (1 - task.split);
      }

      return {
        ...task,
        value,
        favourite,
        points: rawScore,

        primaryPoints,
        secondaryPoints,
        primaryMaxPoints,
        secondaryMaxPoints,
      };
    });

    return { ...category, tasks };
  });

  return firstPass.map((category) => {
    const tasks = category.tasks.map((task) => {
      if ((task.type !== "LINEAR_REF" && task.type !== "PARABOLIC_REF") || task.refValId === null) return task;

      const refTask = firstPass.flatMap((c) => c.tasks).find((t) => t.id === task.refValId);
      const refVal = refTask !== undefined ? refTask.value : 0;
      const rawScore = calculateTaskScore(task.type, task.value, task.maxPoints, task.multiplier, refVal);

      let primaryMaxPoints: number | undefined = undefined;
      let primaryPoints: number | undefined = undefined;
      let secondaryMaxPoints: number | undefined = undefined;
      let secondaryPoints: number | undefined = undefined;
      if (task.secondaryGroupId !== null) {
        primaryPoints = rawScore * task.split;
        secondaryPoints = rawScore * (1 - task.split);
        primaryMaxPoints = task.maxPoints * task.split;
        secondaryMaxPoints = task.maxPoints * (1 - task.split);
      }

      return {
        ...task,
        points: rawScore,

        primaryPoints,
        secondaryPoints,
        primaryMaxPoints,
        secondaryMaxPoints,
      };
    });

    const collectedSplitPoints = tasks.reduce((prev, t) => prev + (t.secondaryGroupId === null ? t.points : t.secondaryGroupId === category.id ? t.secondaryPoints as number : t.primaryPoints as number), 0);
    const maxSplitPoints = tasks.reduce((prev, t) => prev + (t.secondaryGroupId === null ? t.maxPoints : t.secondaryGroupId === category.id ? t.secondaryMaxPoints as number : t.primaryMaxPoints as number), 0);
    const maxFilteredSplitPoints = tasks.filter((t) => t.favourite).reduce((prev, t) => prev + (t.secondaryGroupId === null ? t.maxPoints : t.secondaryGroupId === category.id ? t.secondaryMaxPoints as number : t.primaryMaxPoints as number), 0);
    const achievedSymbol: Category["achievedSymbol"] = collectedSplitPoints >= category.puszczanskaThreshold ? "PUSZCZANSKA" : collectedSplitPoints >= category.lesnaThreshold ? "LESNA" : "POLOWA";

    return {
      ...category,
      tasks,

      collectedSplitPoints,
      maxSplitPoints,
      maxFilteredSplitPoints,

      achievedSymbol,
    };
  });
};

// Apply the overlay's checkbox state to the raw initial-task list.
export const computeInitialTasks = (base: Task[], overlay: Overlay): Task[] => {
  return base.map((task) => {
    return {
      ...task,
      value: overlay.initial[task.id] ? 1 : 0,
    };
  });
};

// Port of backend/src/routes/categorization/index.ts getCategory
const computeCategory = (polowa: number, lesna: number, puszczanska: number, lesnaLesneThreshold: number, lesnaPuszczanskieThreshold: number, puszczanskaLesneThreshold: number, puszczanskaPuszczanskieThreshold: number) => {
  let category = 'POLOWA';

  const effectiveLesnaTokens = lesna + puszczanska;

  if (
    puszczanska >= puszczanskaPuszczanskieThreshold &&
    effectiveLesnaTokens - puszczanskaPuszczanskieThreshold >= puszczanskaLesneThreshold
  ) {
    category = 'PUSZCZANSKA';
  } else if (
    puszczanska >= lesnaPuszczanskieThreshold &&
    effectiveLesnaTokens - lesnaPuszczanskieThreshold >= lesnaLesneThreshold
  ) {
    category = 'LESNA';
  }

  let nextCategory = 'LESNA';
  if (category === 'LESNA') nextCategory = 'PUSZCZANSKA';

  let requiredEffective = 0;
  let requiredPuszczanska = 0;

  if (nextCategory === 'LESNA') {
    requiredEffective = lesnaLesneThreshold;
    requiredPuszczanska = lesnaPuszczanskieThreshold;
  } else if (nextCategory === 'PUSZCZANSKA') {
    requiredEffective = puszczanskaLesneThreshold;
    requiredPuszczanska = puszczanskaPuszczanskieThreshold;
  }

  const missingPuszczanska = Math.max(0, requiredPuszczanska - puszczanska);
  const effectiveAfterPuszczanska = Math.max(0, effectiveLesnaTokens - missingPuszczanska);
  const missingEffective = Math.max(0, requiredEffective - effectiveAfterPuszczanska);

  return {
    category,
    nextCategory,
    tokens: {
      polowa: polowa,
      lesna: lesna,
      puszczanska: puszczanska,
    },
    missingTokens: {
      lesna: missingEffective,
      puszczanska: missingPuszczanska,
    },
  };
};

// Overall category/tokens/points for the StatsBar - mirrors GET /categorization/category.
export const computeCategoryInfo = (categories: Category[], details: CategorizationDetails): CategoryInfo => {
  const polowa = categories.filter((c) => c.achievedSymbol === 'POLOWA').length;
  const lesna = categories.filter((c) => c.achievedSymbol === 'LESNA').length;
  const puszczanska = categories.filter((c) => c.achievedSymbol === 'PUSZCZANSKA').length;

  const result = computeCategory(polowa, lesna, puszczanska, details.lesnaLesneThreshold, details.lesnaPuszczanskieThreshold, details.puszczanskaLesneThreshold, details.puszczanskaPuszczanskieThreshold);

  return {
    ...result,
    points: categories.reduce((prev, c) => prev + c.collectedSplitPoints, 0),
  };
};
